import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  FileCheck,
  Plus,
  AlertTriangle,
  XCircle,
  CheckCircle,
  Calendar,
  Loader2,
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────────────────

type ComplianceItemType =
  | 'insurance'
  | 'registration'
  | 'driver_license'
  | 'dot_inspection'
  | 'medical_cert'
  | 'permit'
  | 'emission_test';

type ComplianceStatus = 'current' | 'expiring_soon' | 'expired' | 'not_applicable';

interface ComplianceItem {
  id: string;
  item_type: ComplianceItemType;
  status: ComplianceStatus;
  description: string;
  vehicle_name?: string;
  driver_name?: string;
  expiry_date: string;
  days_until_expiry?: number;
}

interface ComplianceResponse {
  data: ComplianceItem[];
  total: number;
}

interface ComplianceStats {
  compliance_score: number;
  current_count: number;
  expiring_soon_count: number;
  expiring_30_days_count: number;
  expired_count: number;
  total: number;
  type_counts: Record<string, number>;
}

// ─── Mock Data ─────────────────────────────────────────────────

const MOCK_ITEMS: ComplianceResponse = {
  data: [
    { id: '1', item_type: 'insurance', status: 'current', description: 'Commercial Auto Policy', vehicle_name: 'Truck-003', expiry_date: '2026-11-30', days_until_expiry: 265 },
    { id: '2', item_type: 'registration', status: 'expiring_soon', description: 'Vehicle Registration', vehicle_name: 'Van-012', expiry_date: '2026-04-15', days_until_expiry: 36 },
    { id: '3', item_type: 'driver_license', status: 'current', description: 'CDL Class A', driver_name: 'James Okafor', expiry_date: '2027-06-30', days_until_expiry: 477 },
    { id: '4', item_type: 'dot_inspection', status: 'expiring_soon', description: 'Annual DOT Inspection', vehicle_name: 'Truck-003', expiry_date: '2026-03-25', days_until_expiry: 15 },
    { id: '5', item_type: 'medical_cert', status: 'expired', description: 'DOT Medical Certificate', driver_name: 'Carlos Rivera', expiry_date: '2026-03-01', days_until_expiry: -9 },
    { id: '6', item_type: 'emission_test', status: 'current', description: 'State Emissions Test', vehicle_name: 'Car-007', expiry_date: '2027-01-15', days_until_expiry: 311 },
  ],
  total: 6,
};

const MOCK_STATS: ComplianceStats = {
  compliance_score: 83,
  current_count: 3,
  expiring_soon_count: 2,
  expiring_30_days_count: 1,
  expired_count: 1,
  total: 6,
  type_counts: { insurance: 1, registration: 1, driver_license: 1, dot_inspection: 1, medical_cert: 1, emission_test: 1 },
};

const TYPE_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All Types' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'registration', label: 'Registration' },
  { value: 'driver_license', label: 'Driver License' },
  { value: 'dot_inspection', label: 'DOT Inspection' },
  { value: 'medical_cert', label: 'Medical Cert' },
  { value: 'permit', label: 'Permit' },
  { value: 'emission_test', label: 'Emission Test' },
];

// ─── Helpers ───────────────────────────────────────────────────

function daysColor(days: number | undefined): string {
  if (days == null) return 'var(--muted-foreground)';
  if (days <= 0) return '#EF4444';
  if (days <= 7) return '#EF4444';
  if (days <= 30) return '#F97316';
  if (days <= 90) return '#EAB308';
  return '#10B981';
}

function statusStyle(status: ComplianceStatus): { bg: string; text: string; border: string } {
  const map: Record<ComplianceStatus, { bg: string; text: string; border: string }> = {
    current:       { bg: 'rgba(16,185,129,0.15)',  text: '#10B981', border: 'rgba(16,185,129,0.3)' },
    expiring_soon: { bg: 'rgba(234,179,8,0.15)',   text: '#EAB308', border: 'rgba(234,179,8,0.3)' },
    expired:       { bg: 'rgba(239,68,68,0.15)',   text: '#EF4444', border: 'rgba(239,68,68,0.3)' },
    not_applicable:{ bg: 'rgba(156,163,175,0.1)', text: '#6B7280', border: 'rgba(156,163,175,0.2)' },
  };
  return map[status] ?? { bg: 'rgba(156,163,175,0.15)', text: '#9CA3AF', border: 'rgba(156,163,175,0.3)' };
}

const STATUS_LABEL: Record<ComplianceStatus, string> = {
  current: 'Current',
  expiring_soon: 'Expiring Soon',
  expired: 'Expired',
  not_applicable: 'N/A',
};

const TYPE_LABEL: Record<string, string> = {
  insurance: 'Insurance',
  registration: 'Registration',
  driver_license: 'Driver License',
  dot_inspection: 'DOT Inspection',
  medical_cert: 'Medical Cert',
  permit: 'Permit',
  emission_test: 'Emission Test',
};

function typeStyle(itemType: string): { bg: string; text: string } {
  const map: Record<string, { bg: string; text: string }> = {
    insurance:      { bg: 'rgba(59,130,246,0.15)',  text: '#3B82F6' },
    registration:   { bg: 'rgba(168,85,247,0.15)',  text: '#A855F7' },
    driver_license: { bg: 'rgba(6,182,212,0.15)',   text: '#06B6D4' },
    dot_inspection: { bg: 'rgba(249,115,22,0.15)',  text: '#F97316' },
    medical_cert:   { bg: 'rgba(236,72,153,0.15)',  text: '#EC4899' },
    permit:         { bg: 'rgba(16,185,129,0.15)',  text: '#10B981' },
    emission_test:  { bg: 'rgba(234,179,8,0.15)',   text: '#EAB308' },
  };
  return map[itemType] ?? { bg: 'rgba(156,163,175,0.15)', text: '#9CA3AF' };
}

// ─── Page ──────────────────────────────────────────────────────

export default function CompliancePage() {
  const [typeFilter, setTypeFilter] = useState('');

  const apiUrl = typeFilter ? `/api/compliance?item_type=${typeFilter}` : '/api/compliance';
  const { data: itemsData, isLoading } = useApi<ComplianceResponse>(apiUrl);
  const { data: statsData } = useApi<ComplianceStats>('/api/compliance/stats');

  const items = itemsData?.data ?? MOCK_ITEMS.data;
  const stats = statsData ?? MOCK_STATS;

  const filteredItems = typeFilter
    ? items.filter(i => i.item_type === typeFilter)
    : items;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: 'rgba(59,130,246,0.1)' }}>
            <FileCheck className="w-6 h-6" style={{ color: '#3B82F6' }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>Compliance</h1>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Insurance, registration, and certification tracking</p>
          </div>
        </div>
        <button
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
          style={{ background: '#3B82F6' }}
        >
          <Plus className="w-4 h-4" />
          Add Item
        </button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <ComplianceStatCard
          icon={FileCheck}
          label="Compliance Score"
          value={`${stats.compliance_score}%`}
          color={stats.compliance_score >= 80 ? '#10B981' : stats.compliance_score >= 60 ? '#EAB308' : '#EF4444'}
        />
        <ComplianceStatCard
          icon={AlertTriangle}
          label="Expiring Soon"
          value={String(stats.expiring_soon_count)}
          color="#EAB308"
          sub={`${stats.expiring_30_days_count} within 30 days`}
        />
        <ComplianceStatCard
          icon={XCircle}
          label="Expired"
          value={String(stats.expired_count)}
          color="#EF4444"
        />
        <ComplianceStatCard
          icon={CheckCircle}
          label="Current"
          value={String(stats.current_count)}
          color="#10B981"
          sub={`of ${stats.total} total`}
        />
      </div>

      {/* Type Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        {TYPE_FILTERS.map(f => (
          <button
            key={f.value}
            onClick={() => setTypeFilter(f.value)}
            className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
            style={{
              background: typeFilter === f.value ? '#3B82F6' : 'rgba(59,130,246,0.1)',
              color: typeFilter === f.value ? '#fff' : '#3B82F6',
              border: `1px solid ${typeFilter === f.value ? '#3B82F6' : 'rgba(59,130,246,0.3)'}`,
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div
        className="rounded-xl overflow-hidden"
        style={{
          background: 'var(--glass-bg)',
          border: '1px solid var(--glass-border)',
          backdropFilter: 'blur(var(--glass-blur))',
        }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-16" style={{ color: 'var(--muted-foreground)' }}>
            <FileCheck className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p>No compliance items found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--glass-border)' }}>
                  {['Description', 'Type', 'Vehicle / Driver', 'Expiry Date', 'Days Remaining', 'Status'].map(h => (
                    <th
                      key={h}
                      className="text-left text-xs font-medium uppercase tracking-wide px-4 py-3"
                      style={{ color: 'var(--muted-foreground)' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => {
                  const ss = statusStyle(item.status);
                  const ts = typeStyle(item.item_type);
                  const dc = daysColor(item.days_until_expiry);

                  return (
                    <tr
                      key={item.id}
                      className="border-b transition-colors hover:bg-white/5"
                      style={{ borderColor: 'var(--glass-border)' }}
                    >
                      {/* Description */}
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>
                          {item.description}
                        </span>
                      </td>

                      {/* Type Badge */}
                      <td className="px-4 py-3">
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{ background: ts.bg, color: ts.text }}
                        >
                          {TYPE_LABEL[item.item_type] ?? item.item_type}
                        </span>
                      </td>

                      {/* Vehicle / Driver */}
                      <td className="px-4 py-3">
                        <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                          {item.vehicle_name ?? item.driver_name ?? '—'}
                        </span>
                      </td>

                      {/* Expiry Date */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" style={{ color: 'var(--muted-foreground)' }} />
                          <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                            {new Date(item.expiry_date).toLocaleDateString()}
                          </span>
                        </div>
                      </td>

                      {/* Days Remaining */}
                      <td className="px-4 py-3">
                        <span
                          className="text-sm font-semibold tabular-nums"
                          style={{ color: dc }}
                        >
                          {item.days_until_expiry == null
                            ? '—'
                            : item.days_until_expiry <= 0
                              ? `${Math.abs(item.days_until_expiry)}d overdue`
                              : `${item.days_until_expiry}d`}
                        </span>
                      </td>

                      {/* Status Badge */}
                      <td className="px-4 py-3">
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border"
                          style={{ background: ss.bg, color: ss.text, borderColor: ss.border }}
                        >
                          {STATUS_LABEL[item.status] ?? item.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────

function ComplianceStatCard({
  icon: Icon,
  label,
  value,
  color,
  sub,
}: {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  label: string;
  value: string;
  color: string;
  sub?: string;
}) {
  return (
    <div
      className="rounded-xl p-5"
      style={{
        background: 'var(--glass-bg)',
        border: '1px solid var(--glass-border)',
        backdropFilter: 'blur(var(--glass-blur))',
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color }} />
        <span className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
          {label}
        </span>
      </div>
      <span className="text-2xl font-bold" style={{ color }}>{value}</span>
      {sub && (
        <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>{sub}</p>
      )}
    </div>
  );
}
